import { HttpParams } from "@angular/common/http";
import {
  DatatablesDataColumn,
  DatatablesInput,
  DatatablesMapperFilter,
  DatatablesMapperOptions,
  DatatablesOutput,
  GroupConfig,
  SelectTableRow,
} from "../models";

export class DatatablesMapper {
  /**
   * Maps the datatable paging object to the http params.
   *
   * @author Mateusz Warzyc
   * @param input {@link DatatablesInput } the standard datatables net paging input object
   * @param filters {@link Array<SimpleFilterValueDto>} list of filter values
   * @param options {@link DatatablesMapperOptions} options object that defines the mapping settings
   * @return {@link HttpParams}
   */
  static toHttpParams(
    input: DatatablesInput,
    filters?: DatatablesMapperFilter[],
    options?: DatatablesMapperOptions
  ): HttpParams {
    if (!!filters && filters.length > 0) {
      if (options && options.groupFilterProperties && options.groupFilterProperties.length > 0) {
        filters = this.#groupFilterValues(filters, options.groupFilterProperties);
      }
      filters.forEach((f) => {
        if (!!f.type && !!f.value) {
          if (f.type === "searchBy" && !!f.value) {
            input.search.value = f.value;
          } else {
            const filteredColumn = input.columns.findIndex((c: DatatablesDataColumn) => c.name === f.type);
            if (filteredColumn > -1) {
              input.columns[filteredColumn].search.value = f.value;
            }
          }
        }
      });
    }
    return this.#mapParams(input);
  }

  static #groupFilterValues(
    filters: DatatablesMapperFilter[],
    keysToGroup: GroupConfig[],
    defaultDelimiter = "+"
  ): DatatablesMapperFilter[] {
    const groupKeys = keysToGroup.map((kg) => kg.key);
    const toGroup = filters.filter((item) => groupKeys.includes(item.type));
    const notToGroup = filters.filter((item) => !groupKeys.includes(item.type));

    const grouped = Array.from(
      toGroup.reduce((acc, current) => {
        const values = acc.get(current.type) || [];
        acc.set(current.type, [...values, current.value]);
        return acc;
      }, new Map<string, string[]>())
    ).map(([key, values]) => {
      const groupConfig = keysToGroup.find((group) => group.key === key);
      const delimiter = groupConfig?.delimiter ?? defaultDelimiter;
      const filter: DatatablesMapperFilter = {
        value: values.join(delimiter),
        type: key,
      };
      return filter;
    });

    return [...grouped, ...notToGroup];
  }

  static #mapParams(input: DatatablesInput): HttpParams {
    let paramMap = new HttpParams();
    input.columns.forEach((column, index) => {
      paramMap = paramMap.set("columns[" + index + "].data", column.data);
      paramMap = paramMap.set("columns[" + index + "].name", column.name);
      paramMap = paramMap.set("columns[" + index + "].searchable", column.searchable.toString());
      paramMap = paramMap.set("columns[" + index + "].orderable", column.orderable.toString());
      paramMap = paramMap.set("columns[" + index + "].search.regex", column.search.regex.toString());
      paramMap = paramMap.set("columns[" + index + "].search.value", column.search.value);
    });
    input.order.forEach((order, index) => {
      paramMap = paramMap.set("order[" + index + "].column", order.column.toString());
      paramMap = paramMap.set("order[" + index + "].dir", order.dir);
    });
    paramMap = paramMap.set("search.regex", input.search.regex.toString());
    paramMap = paramMap.set("search.value", input.search.value);
    paramMap = paramMap.set("draw", input.draw.toString());
    paramMap = paramMap.set("start", input.start.toString());
    paramMap = paramMap.set("length", input.length.toString());
    return paramMap;
  }

  static toSelectRows<T>(output: DatatablesOutput<T>, defaultSelection = false): DatatablesOutput<SelectTableRow<T>> {
    return {
      draw: output.draw,
      recordsFiltered: output.recordsFiltered,
      recordsTotal: output.recordsTotal,
      error: output.error,
      data:
        output.data && output.data.length > 0
          ? output.data.map<SelectTableRow<T>>((obj) => ({ selected: defaultSelection, ...obj }))
          : [],
    };
  }
}
